import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  X,
  Eye,
  EyeOff,
  Building2,
  User as UserIcon,
  Mail,
  Phone,
} from 'lucide-react';
import { cn } from '../lib/utils';

const ICON_MAP = {
  building: Building2,
  user: UserIcon,
  mail: Mail,
  phone: Phone,
};

const DynamicFormModal = ({
  isOpen,
  onClose,
  onSave,
  item,
  fields,
  title,
  subtitle,
  submitLabel,
  twoColumns = false,
  maxWidth = 'max-w-lg',
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState({});

  const isEdit = Boolean(item);

  const effectiveFields = useMemo(() => {
    return (fields || []).filter((f) => {
      if (isEdit && f.onlyOnCreate) return false;
      if (!isEdit && f.onlyOnEdit) return false;
      return true;
    });
  }, [fields, isEdit]);

  const visibleFields = useMemo(
    () => effectiveFields.filter((f) => !f.showWhen || f.showWhen(formData, item)),
    [effectiveFields, formData, item],
  );

  useEffect(() => {
    if (!isOpen) return;
    const initialData = effectiveFields.reduce((acc, field) => {
      const defaultValue = field.defaultValue ?? '';
      if (item) {
        const raw = item[field.id];
        acc[field.id] = raw === undefined || raw === null ? defaultValue : raw;
      } else {
        acc[field.id] = defaultValue;
      }
      return acc;
    }, {});
    setFormData(initialData);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setPasswordVisible({});
  }, [isOpen, item, effectiveFields]);

  const handleChange = (id, value, type) => {
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));
    setTouched((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const computedErrors = useMemo(() => {
    const errs = {};
    visibleFields.forEach((field) => {
      if (field.required) {
        const value = formData[field.id];
        const isEmpty = value === undefined || value === null || String(value).trim() === '';
        if (isEmpty && (touched[field.id] || submitAttempted)) {
          errs[field.id] = `${field.label} es requerido`;
        }
      }
    });
    return errs;
  }, [formData, visibleFields, touched, submitAttempted]);

  useEffect(() => {
    setErrors(computedErrors);
  }, [computedErrors]);

  const isFormValid = useMemo(() => {
    return visibleFields.every((field) => {
      if (!field.required) return true;
      const value = formData[field.id];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });
  }, [formData, visibleFields]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    // Recalcular errores con submitAttempted en true
    const errs = {};
    visibleFields.forEach((field) => {
      if (field.required) {
        const value = formData[field.id];
        if (value === undefined || value === null || String(value).trim() === '') {
          errs[field.id] = `${field.label} es requerido`;
        }
      }
    });
    if (Object.keys(errs).length === 0) {
      onSave(formData);
    } else {
      setErrors(errs);
      const allTouched = visibleFields.reduce((acc, f) => ({ ...acc, [f.id]: true }), {});
      setTouched((prev) => ({ ...prev, ...allTouched }));
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className={cn(
          'bg-card p-6 rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto border border-border',
          maxWidth,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar" className="-mt-1 -mr-1">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className={twoColumns ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-3'}>
          {visibleFields.map((field) => (
            <div key={field.id} className="space-y-1.5">
              <Label htmlFor={field.id} className="text-sm font-medium text-foreground">
                {field.label}
                {field.required && <span className="text-destructive"> *</span>}
              </Label>

              {field.type === 'custom' ? (
                field.render ? field.render(formData, setFormData, errors, touched, submitAttempted) : null
              ) : field.type === 'select' ? (
                <select
                  id={field.id}
                  className={cn(
                    'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    errors[field.id] ? 'border-destructive' : 'border-input',
                  )}
                  value={formData[field.id] ?? ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                >
                  <option value="">— Selecciona —</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  id={field.id}
                  rows={field.rows || 3}
                  className={cn(
                    'flex w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    errors[field.id] ? 'border-destructive' : 'border-input',
                  )}
                  value={formData[field.id] ?? ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                />
              ) : (
                <div className="relative">
                  {field.icon && ICON_MAP[field.icon] && (
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">
                      {React.createElement(ICON_MAP[field.icon], { className: 'h-4 w-4' })}
                    </span>
                  )}
                  <Input
                    id={field.id}
                    type={
                      field.type === 'password'
                        ? passwordVisible[field.id]
                          ? 'text'
                          : 'password'
                        : field.type || 'text'
                    }
                    value={formData[field.id] ?? ''}
                    onChange={(e) => handleChange(field.id, e.target.value, field.type)}
                    placeholder={field.placeholder || field.label}
                    className={cn(
                      errors[field.id] && 'border-destructive',
                      field.type === 'password' && 'pr-10',
                      field.icon && ICON_MAP[field.icon] && 'pl-9',
                    )}
                    autoComplete="off"
                  />
                  {field.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((prev) => ({ ...prev, [field.id]: !prev[field.id] }))}
                      className="absolute inset-y-0 right-1.5 my-auto h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                      aria-label={passwordVisible[field.id] ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {passwordVisible[field.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              )}

              {errors[field.id] && (
                <p className="text-sm font-medium text-destructive">{errors[field.id]}</p>
              )}
            </div>
          ))}
          <div className="flex justify-end space-x-2 pt-4 col-span-full">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!isFormValid}>
              {submitLabel || (isEdit ? 'Guardar cambios' : 'Crear')}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default DynamicFormModal;
